import { readVioDataField } from './vio-api-response';

/**
 * Extract a user-visible message from Angular HttpClient failures.
 * Backend often returns ApplicationSuccess-shaped JSON even for 4xx: { Data: { message }, errorDescription }.
 * File downloads use responseType:'blob', so error bodies may be Blob — call enrichHttpErrorForMessage first
 * (CommonApiService.postApiCallForFile does this) or use resolveHttpErrorMessage asynchronously.
 */
export function extractHttpErrorMessage(err: unknown): string {
  if (err == null) return 'Request failed';

  const e = err as {
    error?: unknown;
    message?: string;
    name?: string;
    status?: number;
    statusText?: string;
  };

  const body = e.error;

  if (typeof body === 'string') {
    const t = body.trim();
    if (t.startsWith('{')) {
      try {
        const o = JSON.parse(t) as Record<string, unknown>;
        const from = pickMessageFromApiBody(o);
        if (from) return from;
      } catch {
        /* ignore */
      }
    }
    if (t.length > 0 && !/^http failure response for /i.test(t)) return t;
  }

  if (body && typeof body === 'object' && !(typeof Blob !== 'undefined' && body instanceof Blob)) {
    const from = pickMessageFromApiBody(body as Record<string, unknown>);
    if (from) return from;
  }

  const timeoutMsg = messageIfTimeoutOrNetwork(e);
  if (timeoutMsg) return timeoutMsg;

  if (e.message && !/^http failure response for /i.test(e.message)) {
    const m = e.message.trim();
    if (m) return m;
  }

  if (e.status != null && e.status !== 0) {
    const st = e.statusText ? `${e.status} ${e.statusText}` : String(e.status);
    return `Request failed (${st}). The server did not finish processing — try again.`;
  }

  return 'Request failed. Processing may be incomplete — try again.';
}

/** User-facing copy when the client never got a finished success response. */
export function messageIfTimeoutOrNetwork(err: {
  name?: string;
  message?: string;
  status?: number;
  error?: unknown;
}): string | null {
  const name = String(err?.name || '');
  const msg = String(err?.message || '');
  const blob = `${name} ${msg}`.toLowerCase();

  if (name === 'TimeoutError' || blob.includes('timeout') || blob.includes('timed out')) {
    return (
      'The request timed out before the server finished. ' +
      'Processing may be incomplete — wait a moment and try again, or check server logs if it keeps failing.'
    );
  }

  if (name === 'AbortError' || blob.includes('aborted') || blob.includes('abort')) {
    return (
      'The request was cancelled or timed out before processing finished. ' +
      'You can try again; no need to restart the app.'
    );
  }

  // Angular: status 0 usually means CORS, offline down, or browser/proxy cut the connection mid-request.
  if (err?.status === 0) {
    return (
      'Could not reach the server or the connection dropped before processing finished ' +
      '(timeout, network, or server restart). Try again; if this persists, the backend may still be busy or unreachable.'
    );
  }

  return null;
}

export function pickMessageFromApiBody(o: Record<string, unknown>): string | null {
  const dataRaw = readVioDataField(o);
  if (dataRaw != null && typeof dataRaw === 'object' && !Array.isArray(dataRaw)) {
    const data = dataRaw as Record<string, unknown>;
    const m = data['message'] ?? data['error'];
    if (m != null && String(m).trim()) return String(m).trim();
  }
  const top =
    o['errorDescription'] ??
    o['message'] ??
    (typeof o['error'] === 'string' ? o['error'] : undefined);
  if (top != null && String(top).trim()) return String(top).trim();
  return null;
}

/**
 * When responseType is 'blob', HttpClient puts a Blob in error.error.
 * Parse JSON so extractHttpErrorMessage can read the backend message.
 */
export async function enrichHttpErrorForMessage(err: unknown): Promise<unknown> {
  const e = err as { error?: unknown; message?: string; status?: number; statusText?: string; name?: string };
  if (!e || typeof e !== 'object') return err;
  const body = e.error;
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return err;

  let text = '';
  try {
    text = await body.text();
  } catch {
    return err;
  }
  const t = text.trim();
  if (!t) return err;

  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      const parsed = JSON.parse(t) as Record<string, unknown>;
      return { ...e, error: parsed };
    } catch {
      /* fall through */
    }
  }
  return { ...e, error: { message: t } };
}

/** Async variant for callers that still have a raw Blob error body. */
export async function resolveHttpErrorMessage(err: unknown, fallback = 'Request failed'): Promise<string> {
  const enriched = await enrichHttpErrorForMessage(err);
  const msg = extractHttpErrorMessage(enriched).trim();
  if (!msg || msg === 'Request failed') return fallback;
  return msg;
}
